/**
 * Animaciones sutiles del tema (Gudslip) — scroll-reveal + stats.
 * - Reveal: fade-up con stagger por grupo via IntersectionObserver sobre una
 *   lista curada de selectores (cards, tiles, headings...). Al terminar la
 *   transición se RETIRAN las clases para no pisar los transforms de hover.
 * - Stats: al entrar en viewport, count-up del % y dibujo del anillo (el
 *   dasharray inicial "0" lo pone iurop-reveal.css mientras no hay .is-in).
 * - Sin JS o con prefers-reduced-motion no pasa nada: el contenido queda tal
 *   cual (los estados iniciales ocultos van scoped a html.iurop-motion).
 * Ver NOTAS-DESARROLLO.md (log 2026-07-17).
 */
(function () {
  'use strict';

  if (window.__iuropRevealReady) return;
  window.__iuropRevealReady = true;

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!('IntersectionObserver' in window)) return;

  var STAGGER = 70; // ms entre hermanos del mismo grupo
  var MAX_STEPS = 6; // el stagger se reinicia para no eternizar filas largas

  // Selectores que se revelan (agrupados por su parentNode para el stagger).
  // Solo bloques ESTATICOS de contenido: nada de overlays (sidecart, megamenu,
  // buscador), marquees (ticker, prensa, family cards) ni el buy box de la PDP.
  var TARGETS = [
    // Home / apartados de marca
    '.gudslip-hero__content',
    '.gudslip-goals__heading',
    '.gudslip-goals__card',
    '.gudslip-grid__title',
    '.gudslip-card',
    '.gudslip-split__media',
    '.gudslip-split__cards',
    '.gudslip-stats__text',
    '.gudslip-stats__gauge',
    '.gudslip-community__heading',
    '.gudslip-subscription__content',
    '.gudslip-experts__heading',
    '.gudslip-experts__item',
    '.gudslip-family__heading',
    '.gudslip-brand-tiles__tile',
    '.gudslip-faq__heading',
    '.gudslip-faq__media',
    '.gudslip-faq__item',
    // PLPs
    '.gudslip-colnav__chip',
    // PDP (secciones inferiores)
    '.gudslip-use-cases__card',
    '.gudslip-pdp-expert__media',
    '.gudslip-pdp-expert__usp',
    '.gudslip-compare__intro',
    '.gudslip-compare__table',
    '.gudslip-reviews__card',
    // Crea tu pack
    '.gudslip-pack__chip',
    '.gudslip-pack-card',
    // Paginas (FAQs, contacto, about, 404, buscador)
    '.gudslip-faq-page__heading',
    '.gudslip-faq-page__media',
    '.gudslip-faq-page__item',
    '.gudslip-contact__header',
    '.gudslip-contact__col',
    '.gudslip-about-hero__title',
    '.gudslip-about-story__content',
    '.gudslip-about-story__media',
    '.gudslip-about-mission__card',
    '.gudslip-404__inner',
    '.gudslip-search__count'
  ];

  function init() {
    document.documentElement.classList.add('iurop-motion');

    // El tecleo es independiente del scroll-reveal: se arranca aunque la página
    // no tenga ningún elemento de la lista de reveal.
    initTypewriter();

    var els = document.querySelectorAll(TARGETS.join(','));
    if (!els.length) return;

    // Stagger: índice dentro del grupo de hermanos revelables del mismo padre
    var groups = new Map();
    els.forEach(function (el) {
      var parent = el.parentNode;
      var i = groups.get(parent) || 0;
      groups.set(parent, i + 1);
      el.style.setProperty('--rv-d', (i % MAX_STEPS) * STAGGER + 'ms');
      el.classList.add('iurop-reveal');
    });

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        io.unobserve(el);
        el.classList.add('is-in');
        cleanupLater(el);
        if (el.classList.contains('gudslip-stats__gauge')) countUp(el);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });

    els.forEach(function (el) { io.observe(el); });
  }

  // Efecto máquina de escribir (cita del experto en la PDP, etc.): al entrar en
  // viewport se teclea el texto carácter a carácter. El texto completo ya está
  // en el DOM (SEO/no-JS); solo lo reservamos, vaciamos y re-escribimos.
  // `data-tw-done` evita re-procesar (p.ej. shopify:section:load en el editor).
  function initTypewriter() {
    var nodes = document.querySelectorAll('[data-typewriter]:not([data-tw-done])');
    if (!nodes.length) return;
    var tio = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        tio.unobserve(el);
        typewrite(el);
      });
    }, { threshold: 0.2, rootMargin: '0px 0px -8% 0px' });
    nodes.forEach(function (el) { tio.observe(el); });
  }

  function typewrite(el) {
    if (el.hasAttribute('data-tw-done')) return;
    el.setAttribute('data-tw-done', '');
    var full = el.textContent;
    if (!full) return;
    // Reserva la altura final para que el texto que aparece no empuje el layout
    el.style.minHeight = el.offsetHeight + 'px';
    el.textContent = '';
    el.classList.add('is-typing');
    var i = 0;
    var STEP = 22; // ms por carácter
    function tick() {
      // avanza más rápido por los espacios para un ritmo natural
      do { i++; } while (i < full.length && full.charAt(i - 1) === ' ');
      el.textContent = full.slice(0, i);
      if (i < full.length) {
        window.setTimeout(tick, STEP);
      } else {
        el.classList.remove('is-typing');
        el.style.minHeight = '';
      }
    }
    window.setTimeout(tick, 260);
  }

  // Retira las clases del reveal al acabar (deja el elemento limpio para que
  // los :hover con transform de cada sección funcionen sin especificidad extra)
  function cleanupLater(el) {
    var delay = parseInt(el.style.getPropertyValue('--rv-d'), 10) || 0;
    var done = function () {
      el.classList.remove('iurop-reveal', 'is-in');
      el.style.removeProperty('--rv-d');
    };
    el.addEventListener('transitionend', function onEnd(e) {
      if (e.target !== el || e.propertyName !== 'transform') return;
      el.removeEventListener('transitionend', onEnd);
      done();
    });
    window.setTimeout(done, 900 + delay); // fallback si no llega el evento
  }

  // Count-up del porcentaje del anillo (el dibujo del arco lo hace el CSS)
  function countUp(gauge) {
    var out = gauge.querySelector('.gudslip-stats__percent');
    if (!out) return;
    var target = parseInt(out.textContent, 10);
    if (isNaN(target)) return;
    var t0 = null;
    var DUR = 2600; // sincronizado con el dibujo del anillo (iurop-reveal.scss)
    function tick(ts) {
      if (t0 === null) t0 = ts;
      var p = Math.min((ts - t0) / DUR, 1);
      // lineal, igual que el trazo del anillo: el número sube a ritmo constante
      out.textContent = Math.round(target * p) + '%';
      if (p < 1) window.requestAnimationFrame(tick);
    }
    window.requestAnimationFrame(tick);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
